import { Link } from 'react-router-dom';
import {
  LEP_EA_ID,
  INTERNAL_TENANT,
  COMMAND_CENTER_METRICS,
  MARKETPLACE_VALIDATION_AREAS,
  FEATURE_ACCEPTANCE_FIELDS,
  MODULE_ACCEPTANCE_CATEGORIES,
  AI_ALPHA_VALIDATION,
  UAT_CAPABILITIES,
  FEEDBACK_TYPES,
  BUSINESS_KPIS,
  DOCUMENTATION_AREAS,
  GO_LIVE_ARTIFACTS,
  LESSONS_LEARNED,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_SCORES,
  DEMO_TENANT_OPS,
  DEMO_MARKETPLACE,
  DEMO_FEATURES,
  DEMO_MODULES,
  DEMO_AI_VALIDATION,
  DEMO_UAT,
  DEMO_GO_LIVE,
  buildAcceptanceDashboard,
} from '../../enterpriseacceptance/framework';
import {
  AlphaCommandCenterPanel,
  InternalTenantPanel,
  MarketplaceValidationPanel,
  FeatureAcceptancePanel,
  ModuleAcceptancePanel,
  AlphaAIValidationPanel,
  UserAcceptancePanel,
  GoLiveReadinessPanel,
} from '../../components/enterpriseacceptance';
import { Card, KpiCard } from '../../components/ui';

export function EnterpriseAcceptancePage() {
  const dashboard = buildAcceptanceDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{LEP_EA_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise Acceptance Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          Internal Alpha Program, Enterprise Pilot, Feature Verification & Product Acceptance —
          certify the platform for external customer onboarding using tenant <strong>{INTERNAL_TENANT}</strong>.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Command Center Metrics" value={dashboard.command_center_metrics.toString()} />
        <KpiCard label="Marketplace Areas" value={dashboard.marketplace_areas.toString()} />
        <KpiCard label="Module Categories" value={dashboard.module_categories.toString()} />
        <KpiCard label="Go-Live Artifacts" value={dashboard.go_live_artifacts.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Alpha Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMAND_CENTER_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AlphaCommandCenterPanel scores={DEMO_SCORES} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Internal Tenant — {INTERNAL_TENANT}</h2>
        <p className="mt-1 text-sm text-slate-500">Execute all daily business processes exclusively through the platform</p>
        <Card className="mt-4" padding="sm">
          <InternalTenantPanel tenantName={INTERNAL_TENANT} operations={DEMO_TENANT_OPS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Real Marketplace Validation</h2>
        <p className="mt-1 text-sm text-slate-500">{MARKETPLACE_VALIDATION_AREAS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <MarketplaceValidationPanel validations={DEMO_MARKETPLACE} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Feature Acceptance</h2>
          <p className="mt-1 text-sm text-slate-500">{FEATURE_ACCEPTANCE_FIELDS.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <FeatureAcceptancePanel items={DEMO_FEATURES} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Module Acceptance</h2>
          <p className="mt-1 text-sm text-slate-500">{MODULE_ACCEPTANCE_CATEGORIES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <ModuleAcceptancePanel modules={DEMO_MODULES} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Validation</h2>
        <p className="mt-1 text-sm text-slate-500">{AI_ALPHA_VALIDATION.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AlphaAIValidationPanel results={DEMO_AI_VALIDATION} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">User Acceptance Testing</h2>
        <p className="mt-1 text-sm text-slate-500">{UAT_CAPABILITIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <UserAcceptancePanel sessions={DEMO_UAT} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Go-Live Readiness</h2>
        <p className="mt-1 text-sm text-slate-500">{GO_LIVE_ARTIFACTS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <GoLiveReadinessPanel artifacts={DEMO_GO_LIVE} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Feedback Management</h2>
          <p className="mt-2 text-xs text-slate-500">{FEEDBACK_TYPES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Business KPI Validation</h2>
          <p className="mt-2 text-xs text-slate-500">{BUSINESS_KPIS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Documentation</h2>
          <p className="mt-2 text-xs text-slate-500">{DOCUMENTATION_AREAS.join(' · ')}</p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Lessons Learned</h2>
          <p className="mt-2 text-xs text-slate-500">{LESSONS_LEARNED.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <div className="mt-2 flex flex-wrap gap-1">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
